import { Building2, ChevronDown, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { adminApi } from "@/api/admin";
import type { LookupItem } from "@/types/admin";

export function AgencyMultiSelect({ selected, onChange }: {
  selected: { id: string; name: string }[];
  onChange: (sel: { id: string; name: string }[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<LookupItem[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { adminApi.lookupAgenzie().then(setItems).catch(() => setItems([])); }, []);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);

  const isSel = (id: string) => selected.some((s) => s.id === id);
  const toggle = (it: LookupItem) =>
    onChange(isSel(it.id) ? selected.filter((s) => s.id !== it.id) : [...selected, { id: it.id, name: it.name }]);

  return (
    <div className="relative" ref={ref}>
      <button onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card flex items-center gap-2 hover:bg-surface">
        <Building2 className="w-4 h-4 text-muted-foreground" />
        <span>{selected.length === 0 ? "Tutte le agenzie" : `${selected.length} agenzie`}</span>
        <ChevronDown className="w-4 h-4 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 bg-card border border-border rounded-lg shadow-lg z-40 p-2 max-h-72 overflow-auto">
          <div className="flex items-center justify-between px-2 py-1 text-xs">
            <button className="text-blue-700 hover:underline" onClick={() => onChange(items.map((i) => ({ id: i.id, name: i.name })))}>Tutte</button>
            <button className="text-blue-700 hover:underline" onClick={() => onChange([])}>Nessuna</button>
          </div>
          <div className="mt-1 space-y-0.5">
            {items.map((it) => (
              <label key={it.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface cursor-pointer text-sm">
                <input type="checkbox" checked={isSel(it.id)} onChange={() => toggle(it)} className="rounded border-border" />
                <span className="flex-1">{it.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 text-xs px-2 py-1 rounded-full border border-blue-200">
              {s.name}<button onClick={() => onChange(selected.filter((x) => x.id !== s.id))}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
